/**
 * App.js  ·  ZipperAgent CRM
 *
 * PRODUCTION FLAG:
 *   Set REACT_APP_ADMIN_ONLY=true in your .env to deploy only the /admin section.
 *   Set REACT_APP_ADMIN_ONLY=false to deploy the full CRM (default).
 *   All other routes will show a "Coming Soon" screen when ADMIN_ONLY=true.
 *   The main Sidebar and TopBar are hidden on /admin/* routes — AdminLayout
 *   provides its own navigation.
 */

import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ThemeProvider }    from './contexts/ThemeContext';
import Sidebar              from './components/Sidebar';
import TopBar               from './components/topbar/TopBar';
import SuperAdminBanner     from './components/topbar/SuperAdminBanner';
import InitializeAuth       from './components/InitializeAuth';
import ErrorBoundary        from './components/ErrorBoundary';
import PageSkeleton         from './components/PageSkeleton';

import './styles/App.css';

// ─── Feature flag ─────────────────────────────────────────────────────────────
// Set REACT_APP_ADMIN_ONLY=true in .env.production to deploy admin-only build.
// Set REACT_APP_ADMIN_ONLY=false (or leave unset) for the full CRM.
const ADMIN_ONLY = process.env.REACT_APP_ADMIN_ONLY === 'true';

// ─── Auth pages (always needed) ───────────────────────────────────────────────
const Login               = lazy(() => import('./pages/LoginPage'));
const SuperAdminLogin     = lazy(() => import('./components/SuperAdminLogin'));
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard'));

// ─── Admin pages ──────────────────────────────────────────────────────────────
const AdminOverviewPage  = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminLayout        = lazy(() => import('./pages/admin/AdminLayout'));
const AutomationListPage = lazy(() => import('./pages/admin/follow-up/automations/AutomationListPage'));
const AutomationCanvas   = lazy(() => import('./pages/admin/follow-up/automations/AutomationCanvas'));

const LeadFlow            = lazy(() => import('./pages/admin/lead-distribution/LeadFlow'));
const Groups              = lazy(() => import('./pages/admin/lead-distribution/Groups'));
const Ponds               = lazy(() => import('./pages/admin/lead-distribution/Ponds'));

const ActionPlans         = lazy(() => import('./pages/admin/follow-up/ActionPlans'));
const BirthdayAnniversary = lazy(() => import('./pages/admin/follow-up/BirthdayAnniversary'));
const EmailTemplates      = lazy(() => import('./pages/admin/follow-up/EmailTemplates'));
const TextTemplates       = lazy(() => import('./pages/admin/follow-up/TextTemplates'));

const AdminCompany        = lazy(() => import('./pages/admin/account/Company'));
const PhoneNumbers        = lazy(() => import('./pages/admin/account/PhoneNumbers'));
const AdminEmails         = lazy(() => import('./pages/admin/account/Emails'));
const Import              = lazy(() => import('./pages/admin/account/Import'));
const Calling             = lazy(() => import('./pages/admin/account/Calling'));
const AdminUsers          = lazy(() => import('./pages/admin/account/Users'));
const Teams               = lazy(() => import('./pages/admin/account/Teams'));

const StatusPage          = lazy(() => import('./pages/admin/customize/Status'));
const Stages              = lazy(() => import('./pages/admin/customize/Stages'));
const Deals               = lazy(() => import('./pages/admin/customize/Deals'));
const Source              = lazy(() => import('./pages/admin/customize/Source'));
const Fields              = lazy(() => import('./pages/admin/customize/Fields'));
const Tags                = lazy(() => import('./pages/admin/customize/Tags'));
const Appointments        = lazy(() => import('./pages/admin/customize/Appointments'));
const CRMWorkflow         = lazy(() => import('./pages/admin/customize/Workflow'));

const Integrations        = lazy(() => import('./pages/admin/integrations/IntegrationSettings'));

const BillingPage         = lazy(() => import('./pages/admin/billing/Billing'));
const DataUsage           = lazy(() => import('./pages/admin/billing/DataUsage'));

const IDXWebsite          = lazy(() => import('./pages/admin/idx/IDXWebsite'));
const MLS                 = lazy(() => import('./pages/admin/idx/MLS'));
const PropertyAlerts      = lazy(() => import('./pages/admin/idx/PropertyAlerts'));
const MarketReports       = lazy(() => import('./pages/admin/idx/MarketReports'));

const AdminNotifications  = lazy(() => import('./pages/admin/notifications/Notifications'));
const EmailCCOptions      = lazy(() => import('./pages/admin/notifications/EmailCCOptions'));

const BlogPreference      = lazy(() => import('./pages/admin/onboarding/BlogPreference'));
const Communities         = lazy(() => import('./pages/admin/onboarding/Communities'));
const Biography           = lazy(() => import('./pages/admin/onboarding/Biography'));
const Photos              = lazy(() => import('./pages/admin/onboarding/Photos'));
const AdminTemplates      = lazy(() => import('./pages/admin/onboarding/Templates'));
const Colors              = lazy(() => import('./pages/admin/onboarding/Colors'));
const Logos               = lazy(() => import('./pages/admin/onboarding/Logos'));
const AdminNotes          = lazy(() => import('./pages/admin/onboarding/Notes'));

// ─── Full CRM pages (loaded when ADMIN_ONLY=false) ────────────────────────────
const HomePage              = lazy(() => import('./pages/HomePage'));
const DashboardPage         = lazy(() => import('./pages/DashboardPage'));
const ReportsPage           = lazy(() => import('./pages/reports/ReportsPage'));
const Contacts              = lazy(() => import('./pages/Contacts'));
const Companies             = lazy(() => import('./pages/Companies'));
const Calendar              = lazy(() => import('./pages/Calendar'));
const Notes                 = lazy(() => import('./pages/Notes'));
const Email                 = lazy(() => import('./pages/Email'));
const EditProfilePage       = lazy(() => import('./components/topbar/profile/EditProfilePage'));
const NotificationPrefPage  = lazy(() => import('./components/topbar/profile/NotificationPrefPage'));
const HelpSupportPage       = lazy(() => import('./components/topbar/profile/HelpSupportPage'));

// ─── Coming Soon screen (used when ADMIN_ONLY=true) ───────────────────────────
function ComingSoon() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#f0f9ff,#f8fafc)',
      fontFamily: "'DM Sans',-apple-system,sans-serif", padding: 24,
    }}>
      <img
        src="/images/za-logo-all.png"
        alt="ZipperAgent"
        style={{ height: 44, marginBottom: 32, objectFit: 'contain' }}
        onError={e => e.target.style.display = 'none'}
      />
      <div style={{
        background: '#fff', borderRadius: 20, border: '1px solid #e4e9f0',
        padding: '48px 56px', maxWidth: 480, width: '100%', textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
          background: 'linear-gradient(135deg,#ecfeff,#cffafe)',
          border: '1.5px solid #a5f3fc', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 32, color: '#0891b2' }}>rocket_launch</span>
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 10px', letterSpacing: '-0.02em' }}>
          Coming Soon
        </h2>
        <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 28px' }}>
          This section is currently under development. Head to Admin to manage your settings.
        </p>
        <a
          href="/admin"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '11px 24px', borderRadius: 10,
            background: 'linear-gradient(135deg,#0891b2,#06b6d4)',
            color: '#fff', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', boxShadow: '0 2px 10px rgba(8,145,178,0.3)',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>admin_panel_settings</span>
          Go to Admin
        </a>
      </div>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 24 }}>
        ZipperAgent CRM · Admin Portal
      </p>
    </div>
  );
}

// ─── Prefetch map ─────────────────────────────────────────────────────────────
const PREFETCH_MAP = {
  admin: () => import('./pages/admin/AdminOverviewPage'),
  ...(!ADMIN_ONLY && {
    home:      () => import('./pages/HomePage'),
    dashboard: () => import('./pages/DashboardPage'),
    reports:   () => import('./pages/reports/ReportsPage'),
    contacts:  () => import('./pages/Contacts'),
    calendar:  () => import('./pages/Calendar'),
    notes:     () => import('./pages/Notes'),
    email:     () => import('./pages/Email'),
  }),
};

export function preloadRoute(key) {
  const loader = PREFETCH_MAP[key];
  if (loader) loader();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getSlugFromPath = () => {
  const parts = window.location.pathname.replace(/^\//, '').split('/');
  const slug  = parts[0].toLowerCase();
  const reserved = [
    'super-admin-login', 'home', 'dashboard', 'contacts', 'reports',
    'activities', 'inbox', 'settings', 'admin', 'tasks',
    'automations', 'profile', 'support', '',
  ];
  return reserved.includes(slug) ? null : slug;
};

const getClientBySlug = (slug) => {
  if (!slug) return null;
  const clients = JSON.parse(localStorage.getItem('clients') || '[]');
  return clients.find(c => (c.crmEmail || '').split('@')[0].toLowerCase() === slug) || null;
};

// ─── All admin routes (shared between both modes) ─────────────────────────────
function AdminRoutes({ currentUser }) {
  return (
    <Route path="/admin" element={<AdminLayout currentUser={currentUser} />}>
      <Route index                                        element={<AdminOverviewPage   currentUser={currentUser} />} />
      <Route path="lead-distribution/lead-flow"          element={<LeadFlow            currentUser={currentUser} />} />
      <Route path="lead-distribution/groups"             element={<Groups              currentUser={currentUser} />} />
      <Route path="lead-distribution/ponds"              element={<Ponds               currentUser={currentUser} />} />
      <Route path="follow-up/action-plans"               element={<ActionPlans         currentUser={currentUser} />} />
      <Route path="follow-up/birthday-anniversary"       element={<BirthdayAnniversary currentUser={currentUser} />} />
      <Route path="follow-up/email-templates"            element={<EmailTemplates      currentUser={currentUser} />} />
      <Route path="follow-up/automations"                element={<AutomationListPage  currentUser={currentUser} />} />
      <Route path="follow-up/automations/canvas/:id"     element={<AutomationCanvas    currentUser={currentUser} />} />
      <Route path="follow-up/text-templates"             element={<TextTemplates       currentUser={currentUser} />} />
      <Route path="account/company"                      element={<AdminCompany        currentUser={currentUser} />} />
      <Route path="account/phone-numbers"                element={<PhoneNumbers        currentUser={currentUser} />} />
      <Route path="account/emails"                       element={<AdminEmails         currentUser={currentUser} />} />
      <Route path="account/import"                       element={<Import              currentUser={currentUser} />} />
      <Route path="account/calling"                      element={<Calling             currentUser={currentUser} />} />
      <Route path="account/users"                        element={<AdminUsers          currentUser={currentUser} />} />
      <Route path="account/teams"                        element={<Teams               currentUser={currentUser} />} />
      <Route path="customize/status"                     element={<StatusPage          currentUser={currentUser} />} />
      <Route path="customize/stages"                     element={<Stages              currentUser={currentUser} />} />
      <Route path="customize/deals"                      element={<Deals               currentUser={currentUser} />} />
      <Route path="customize/source"                     element={<Source              currentUser={currentUser} />} />
      <Route path="customize/fields"                     element={<Fields              currentUser={currentUser} />} />
      <Route path="customize/tags"                       element={<Tags                currentUser={currentUser} />} />
      <Route path="customize/appointments"               element={<Appointments        currentUser={currentUser} />} />
      <Route path="customize/workflow"                   element={<CRMWorkflow         currentUser={currentUser} />} />
      <Route path="integrations"                         element={<Integrations        currentUser={currentUser} />} />
      <Route path="billing/billing"                      element={<BillingPage         currentUser={currentUser} />} />
      <Route path="billing/data-usage"                   element={<DataUsage           currentUser={currentUser} />} />
      <Route path="idx/website"                          element={<IDXWebsite          currentUser={currentUser} />} />
      <Route path="idx/mls"                              element={<MLS                 currentUser={currentUser} />} />
      <Route path="idx/property-alerts"                  element={<PropertyAlerts      currentUser={currentUser} />} />
      <Route path="idx/market-reports"                   element={<MarketReports       currentUser={currentUser} />} />
      <Route path="notifications/notifications"          element={<AdminNotifications  currentUser={currentUser} />} />
      <Route path="notifications/email-cc-options"       element={<EmailCCOptions      currentUser={currentUser} />} />
      <Route path="onboarding/blog-preference"           element={<BlogPreference      currentUser={currentUser} />} />
      <Route path="onboarding/communities"               element={<Communities         currentUser={currentUser} />} />
      <Route path="onboarding/biography"                 element={<Biography           currentUser={currentUser} />} />
      <Route path="onboarding/photos"                    element={<Photos              currentUser={currentUser} />} />
      <Route path="onboarding/templates"                 element={<AdminTemplates      currentUser={currentUser} />} />
      <Route path="onboarding/colors"                    element={<Colors              currentUser={currentUser} />} />
      <Route path="onboarding/logos"                     element={<Logos               currentUser={currentUser} />} />
      <Route path="onboarding/notes"                     element={<AdminNotes          currentUser={currentUser} />} />
    </Route>
  );
}

// ─── AppRoutes — ADMIN ONLY mode ──────────────────────────────────────────────
function AdminOnlyRoutes({ currentUser, handleLogout }) {
  const location = useLocation();
  return (
    <>
      <SuperAdminBanner currentUser={currentUser} onExitView={handleLogout} />
      <div className={`app-root ${currentUser?.isSuperAdminView ? 'has-super-admin-banner' : ''}`}>
        <main className="main-content" style={{ marginLeft: 0 }}>
          <div className="page-wrap page-wrap--admin-only">
            <ErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  <Route path="/"      element={<Navigate to="/admin" replace />} />
                  <Route path="/home"  element={<Navigate to="/admin" replace />} />
                  {AdminRoutes({ currentUser })}
                  <Route path="/super-admin-login" element={<Navigate to="/admin" replace />} />
                  <Route path="*" element={<ComingSoon />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </>
  );
}

// ─── AppRoutes — FULL CRM mode ────────────────────────────────────────────────
function FullCRMRoutes({ currentUser, superAdmin, handleLogout }) {
  const location     = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [wasOnAdmin,       setWasOnAdmin]       = useState(false);

  useEffect(() => {
    if (isAdminRoute && !wasOnAdmin) {
      setSidebarCollapsed(true);
      setWasOnAdmin(true);
    } else if (!isAdminRoute && wasOnAdmin) {
      setSidebarCollapsed(false);
      setWasOnAdmin(false);
    }
  }, [isAdminRoute, wasOnAdmin]);

  const handleSidebarCollapsedChange = useCallback((next) => {
    setSidebarCollapsed(next);
  }, []);

  return (
    <>
      <SuperAdminBanner currentUser={currentUser} onExitView={handleLogout} />
      <div className={`app-root ${currentUser?.isSuperAdminView ? 'has-super-admin-banner' : ''}`}>
        {!isAdminRoute && (
          <Sidebar
            currentUser={currentUser}
            preloadRoute={preloadRoute}
            collapsed={sidebarCollapsed}
            onCollapsedChange={handleSidebarCollapsedChange}
          />
        )}
        <main className={`main-content${isAdminRoute ? ' main-content--no-sidebar' : ''}`}>
          {!isAdminRoute && (
            <TopBar currentUser={currentUser} superAdmin={superAdmin} onLogout={handleLogout} />
          )}
          <div className={`page-wrap${isAdminRoute ? ' page-wrap--admin' : ''}`}>
            <ErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {/* Root → home */}
                  <Route path="/"          element={<Navigate to="/home" replace />} />

                  {/* Main CRM pages */}
                  <Route path="/home"      element={<HomePage      currentUser={currentUser} />} />
                  <Route path="/dashboard" element={<DashboardPage currentUser={currentUser} />} />

                  {/* Tenant/slug-based routes */}
                  <Route path="/:slug/home"      element={<HomePage      currentUser={currentUser} />} />
                  <Route path="/:slug/dashboard" element={<DashboardPage currentUser={currentUser} />} />

                  {/* CRM sections */}
                  <Route path="/reports"             element={<ReportsPage currentUser={currentUser} />} />
                  <Route path="/contacts"            element={<Contacts    currentUser={currentUser} />} />
                  <Route path="/contacts/companies"  element={<Companies   currentUser={currentUser} />} />
                  <Route path="/activities/calendar" element={<Calendar    currentUser={currentUser} />} />
                  <Route path="/activities/notes"    element={<Notes       currentUser={currentUser} />} />
                  <Route path="/inbox/email"         element={<Email       currentUser={currentUser} />} />

                  {/* Profile routes */}
                  <Route path="/profile/edit"          element={<EditProfilePage      currentUser={currentUser} />} />
                  <Route path="/profile/notifications" element={<NotificationPrefPage currentUser={currentUser} />} />
                  <Route path="/support"               element={<HelpSupportPage      currentUser={currentUser} />} />

                  {/* All admin routes */}
                  {AdminRoutes({ currentUser })}

                  {/* Super admin login redirect */}
                  <Route path="/super-admin-login" element={<Navigate to="/home" replace />} />

                  {/* 404 fallback */}
                  <Route path="*" element={
                    <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui,sans-serif' }}>
                      <h2>Page not found</h2>
                      <p>The page you're looking for doesn't exist.</p>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser,     setCurrentUser]     = useState(null);
  const [superAdmin,      setSuperAdmin]      = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [needsInit,       setNeedsInit]       = useState(false);

  useEffect(() => { checkAuthStatus(); }, []);

  const checkAuthStatus = () => {
    const superAdmins = JSON.parse(localStorage.getItem('superAdmins') || '[]');
    if (superAdmins.length === 0) { setNeedsInit(true); setLoading(false); return; }

    const urlParams      = new URLSearchParams(window.location.search);
    const impersonateKey = urlParams.get('impersonate');
    if (impersonateKey) {
      const pending = localStorage.getItem(impersonateKey);
      if (pending) {
        const { userData } = JSON.parse(pending);
        localStorage.removeItem(impersonateKey);
        window.history.replaceState({}, '', window.location.pathname);
        let enriched = { ...userData };
        if (!enriched.clientId) {
          const slug    = window.location.pathname.replace(/^\//, '').split('/')[0].toLowerCase();
          const clients = JSON.parse(localStorage.getItem('clients') || '[]');
          const match   = clients.find(c => (c.crmEmail || '').split('@')[0].toLowerCase() === slug);
          if (match) { enriched.clientId = match.id; enriched.companyName = enriched.companyName || match.companyName; }
        }
        if (!enriched.companyName && enriched.clientId) {
          const clients = JSON.parse(localStorage.getItem('clients') || '[]');
          const match   = clients.find(c => c.id === enriched.clientId);
          if (match) enriched.companyName = match.companyName;
        }
        sessionStorage.setItem('userData', JSON.stringify(enriched));
        setCurrentUser(enriched); setIsAuthenticated(true); setLoading(false);
        window.location.replace('/home'); return;
      }
    }

    const savedSuperAdmin = localStorage.getItem('superAdminData');
    const savedUser       = sessionStorage.getItem('userData');

    if (savedSuperAdmin && savedUser) {
      const saData   = JSON.parse(savedSuperAdmin);
      const userData = JSON.parse(savedUser);
      if (userData.isSuperAdminView) {
        setSuperAdmin(saData); setCurrentUser(userData);
        setIsAuthenticated(true); setLoading(false); return;
      }
    }
    if (savedSuperAdmin && !savedUser) { setSuperAdmin(JSON.parse(savedSuperAdmin)); setLoading(false); return; }
    if (savedUser) { setCurrentUser(JSON.parse(savedUser)); setIsAuthenticated(true); setLoading(false); return; }
    setLoading(false);
  };

  const handleClientLoginSuccess    = useCallback((u)  => { setCurrentUser(u);  setIsAuthenticated(true); }, []);
  const handleSuperAdminLoginSuccess = useCallback((sa) => { setSuperAdmin(sa); }, []);

  const handleLogout = useCallback(() => {
    if (superAdmin && currentUser?.isSuperAdminView) {
      if (window.confirm('Return to Super Admin Dashboard?')) {
        sessionStorage.removeItem('userData');
        setCurrentUser(null); setIsAuthenticated(false); window.location.reload();
      }
      return;
    }
    if (window.confirm('Are you sure you want to logout?')) {
      const slug = (currentUser?.crmEmail || currentUser?.email || '').split('@')[0].toLowerCase();
      localStorage.removeItem('userData');       sessionStorage.removeItem('userData');
      localStorage.removeItem('superAdminData'); sessionStorage.removeItem('superAdminData');
      setCurrentUser(null); setSuperAdmin(null); setIsAuthenticated(false);
      window.location.href = slug ? '/' + slug : '/';
    }
  }, [superAdmin, currentUser]);

  const handleInitComplete = useCallback(() => { setNeedsInit(false); window.location.reload(); }, []);

  if (needsInit) return <InitializeAuth onComplete={handleInitComplete} />;
  if (loading)   return <div className="app-loading"><div className="loading-spinner" /><p>Loading...</p></div>;

  const currentSlug  = getSlugFromPath();
  const tenantClient = getClientBySlug(currentSlug);

  const AuthScreen = ({ children }) => (
    <Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}>
      {children}
    </Suspense>
  );

  // ─── Auth gate ───────────────────────────────────────────────────────────────
  if (currentSlug && tenantClient && !isAuthenticated)
    return <AuthScreen><Login onLoginSuccess={handleClientLoginSuccess} /></AuthScreen>;

  if (superAdmin && !isAuthenticated)
    return <AuthScreen><SuperAdminDashboard superAdmin={superAdmin} onLogout={handleLogout} /></AuthScreen>;

  if (!isAuthenticated) {
    const path = window.location.pathname;
    if (path === '/super-admin-login')
      return <AuthScreen><SuperAdminLogin onLoginSuccess={handleSuperAdminLoginSuccess} /></AuthScreen>;
    if (currentSlug)
      return <AuthScreen><Login onLoginSuccess={handleClientLoginSuccess} /></AuthScreen>;
    return <AuthScreen><SuperAdminLogin onLoginSuccess={handleSuperAdminLoginSuccess} /></AuthScreen>;
  }

  // ─── Render correct shell based on flag ──────────────────────────────────────
  return (
    <ThemeProvider>
      {ADMIN_ONLY
        ? <AdminOnlyRoutes currentUser={currentUser} handleLogout={handleLogout} />
        : <FullCRMRoutes   currentUser={currentUser} superAdmin={superAdmin} handleLogout={handleLogout} />
      }
    </ThemeProvider>
  );
}